import React, { useState } from "react";
import { auth, db } from "../../firebase/config";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { trackUsageEvent } from "../Analytics/CampaignTrafficTracker";
import { trackAppEvent } from "../../utils/appAnalytics";
import {
  getVerificationErrorMessage,
  sendVerificationEmail,
} from "../../utils/verificationEmail";
import { writeRegistrationSuccessNotice } from "./registrationSuccessNotice";

const LEGAL_VERSION = "2026-06-17";
const TERMS_VERSION = LEGAL_VERSION;
const PRIVACY_VERSION = LEGAL_VERSION;
const COMMUNITY_RULES_VERSION = LEGAL_VERSION;
const SAFETY_GUIDELINES_VERSION = LEGAL_VERSION;
const USE_SERVER_REGISTRATION_PROFILE =
  process.env.REACT_APP_USE_SERVER_REGISTRATION_PROFILE !== "false";
const ALLOW_FREE_REGISTRATION_FALLBACK =
  process.env.REACT_APP_ALLOW_FREE_REGISTRATION_FALLBACK === "true";

const ALLOWED_USER_TYPES = [
  "farmer",
  "landowner",
  "homesteader",
  "day_worker",
  "community",
  "event_planner",
];

const createFreeLocalProfile = async ({ user, name, userType }) => {
  const userRef = doc(db, "users", user.uid);
  const email = (user.email || "").toLowerCase();

  await setDoc(userRef, {
    uid: user.uid,
    name,
    displayName: name,
    email,
    emailLower: email,

    type: userType,
    role: userType,
    roles: [userType],

    equipment: [],
    rating: 0,
    reviewCount: 0,

    bio: "",
    intro: "",
    phone: "",
    profilePicture: null,
    farmAnimal: "goat",
    workImages: [],
    storefrontEnabled: false,

    notifPrefs: {
      chat: true,
      listings: false,
      products: false,
      events: false,
      bids: true,
      reservations: true,
      sound: "default",
    },

    accountStatus: "active",
    deactivatedAt: null,
    bannedAt: null,
    deletedAt: null,

    admin: false,
    isAdmin: false,

    termsAccepted: true,
    termsAcceptedAt: serverTimestamp(),
    ageConfirmed: true,
    ageConfirmedAt: serverTimestamp(),
    legalVersion: LEGAL_VERSION,
    termsVersion: TERMS_VERSION,
    privacyAccepted: true,
    privacyAcceptedAt: serverTimestamp(),
    privacyVersion: PRIVACY_VERSION,
    communityRulesAccepted: true,
    communityRulesAcceptedAt: serverTimestamp(),
    communityRulesVersion: COMMUNITY_RULES_VERSION,
    safetyGuidelinesAccepted: true,
    safetyGuidelinesAcceptedAt: serverTimestamp(),
    safetyGuidelinesVersion: SAFETY_GUIDELINES_VERSION,

    plan: "free",
    founder: false,
    isFounder: false,
    founderAccount: false,
    founderNumber: null,
    founderSpotActive: false,
    subscriptionStatus: "required",
    subscriptionRequired: true,
    premiumAccess: false,

    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return {
    data: {
      created: true,
      getsFounderAccount: false,
      founderNumber: null,
      freeOnlyFallback: true,
    },
  };
};

const Register = ({ embedded = false, switchToLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [userType, setUserType] = useState("farmer");
  const [wantsNotifications, setWantsNotifications] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [legalError, setLegalError] = useState("");
  const [registrationComplete, setRegistrationComplete] = useState(null);
  const [verificationNotice, setVerificationNotice] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const safeStandReturnTo =
    typeof location.state?.returnTo === "string" &&
    location.state.returnTo.startsWith("/stand/") &&
    !location.state.returnTo.startsWith("//")
      ? location.state.returnTo
      : "";

  const handleRegister = async (e) => {
    e.preventDefault();

    const cleanName = name.trim();
    const cleanEmail = email.trim().toLowerCase();

    if (!cleanName) {
      alert("Please enter your name.");
      return;
    }

    if (cleanName.length < 2) {
      alert("Please enter a valid name.");
      return;
    }

    if (cleanName.length > 60) {
      alert("Name must be 60 characters or less.");
      return;
    }

    if (!cleanEmail) {
      alert("Please enter your email.");
      return;
    }

    if (password.length < 6) {
      alert("Password must be at least 6 characters.");
      return;
    }

    if (!ALLOWED_USER_TYPES.includes(userType)) {
      alert("Please choose a valid account type.");
      return;
    }

    if (!acceptedTerms) {
      setLegalError(
        "Please confirm that you are at least 18 years old and agree to the legal terms before creating an account."
      );
      return;
    }

    setLegalError("");

    let profileCreated = false;

    try {
      setRegistering(true);
      trackUsageEvent("registration_started", {
        feature: "registration",
        action: "submit",
      });

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        cleanEmail,
        password
      );

      let founderResult = null;

      if (USE_SERVER_REGISTRATION_PROFILE) {
        const createRegisteredUserProfile = httpsCallable(
          getFunctions(undefined, "us-central1"),
          "createRegisteredUserProfile"
        );

        try {
          founderResult = await createRegisteredUserProfile({
            name: cleanName,
            userType,
            legalVersion: LEGAL_VERSION,
            termsVersion: TERMS_VERSION,
            privacyVersion: PRIVACY_VERSION,
            communityRulesVersion: COMMUNITY_RULES_VERSION,
            safetyGuidelinesVersion: SAFETY_GUIDELINES_VERSION,
          });
        } catch (profileFunctionError) {
          if (!ALLOW_FREE_REGISTRATION_FALLBACK) {
            console.error(
              "Server registration profile function failed; founder-safe signup cannot continue.",
              {
                code: profileFunctionError?.code,
                message: profileFunctionError?.message,
              }
            );
            throw new Error(
              "We could not finish founder account setup. Please try again."
            );
          }

          console.warn(
            "Server registration profile function failed; creating a free local profile because fallback is explicitly enabled.",
            {
              code: profileFunctionError?.code,
              message: profileFunctionError?.message,
            }
          );
          founderResult = await createFreeLocalProfile({
            user: userCredential.user,
            name: cleanName,
            userType,
          });
        }
      } else {
        founderResult = await createFreeLocalProfile({
          user: userCredential.user,
          name: cleanName,
          userType,
        });
      }

      profileCreated = true;
      trackAppEvent("signup_completed", {
        feature: "registration",
        action: "profile_created",
        role: userType,
        status: founderResult?.data?.getsFounderAccount ? "founder" : "created",
      });
      trackUsageEvent("registration_completed", {
        feature: "registration",
        action: "profile_created",
        status: founderResult?.data?.getsFounderAccount ? "founder" : "created",
      });

      let verificationEmailSent = false;
      try {
        await sendVerificationEmail(userCredential.user);
        verificationEmailSent = true;
      } catch (verificationError) {
        console.error("Verification email failed:", {
          code: verificationError?.code,
          message: verificationError?.message,
        });
        setVerificationNotice(getVerificationErrorMessage(verificationError));
      }

      if (wantsNotifications && "Notification" in window) {
        const permission = await Notification.requestPermission();

        if (permission === "granted") {
          new Notification("🌱 MyFarmingPal", {
            body: "Notifications enabled!",
          });
        }
      }

      const loginNoticeNavigation = writeRegistrationSuccessNotice();
      if (loginNoticeNavigation) {
        navigate("/login", loginNoticeNavigation);
      } else {
        navigate("/login");
      }
      setRegistrationComplete({
        founder: founderResult?.data?.getsFounderAccount === true,
        founderNumber: founderResult?.data?.founderNumber || null,
        founderCount: founderResult?.data?.founderCount || founderResult?.data?.founderNumber || null,
        founderLimit: founderResult?.data?.founderLimit || 70,
        verificationEmailSent,
        email: cleanEmail,
      });
    } catch (error) {
      console.error(error);
      trackUsageEvent("form_error_shown", {
        feature: "registration",
        action: "submit",
        status: "error",
        errorCode: error?.code || "registration_failed",
        errorSummary: error?.message || "Registration failed",
      });
      const currentUser = auth.currentUser;

      if (!profileCreated && currentUser && !currentUser.emailVerified) {
        try {
          await currentUser.delete();
        } catch (deleteError) {
          console.error("Could not roll back incomplete registration:", deleteError);
        }
      }

      alert(
        error?.message ||
          "We could not finish registration. Please check your info and try again."
      );
    } finally {
      setRegistering(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setResendingVerification(true);
      setVerificationNotice("");
      await sendVerificationEmail();
      setVerificationNotice("Verification email sent. Please check your inbox.");
    } catch (error) {
      console.error("Verification resend failed:", {
        code: error?.code,
        message: error?.message,
      });
      setVerificationNotice(getVerificationErrorMessage(error));
    } finally {
      setResendingVerification(false);
    }
  };

  if (registrationComplete) {
    const isStillLoggedIn = Boolean(auth.currentUser?.uid);
    const continueLabel = isStillLoggedIn ? "Continue to MyFarmingPal" : "Go to Login";
    const continueTarget = isStillLoggedIn ? safeStandReturnTo || "/home" : "/login";

    return (
      <div className="auth-check-email">
        <div className="auth-hero">
          <div className="auth-badge">Account created</div>
          <h2>Account created successfully</h2>
          <p>
            Your MyFarmingPal account is ready. Please check your email to
            verify your account.
          </p>
        </div>

        <div className="auth-check-email-card">
          {registrationComplete.founder && (
            <div className="auth-founder-success">
              <strong>Founder account claimed</strong>
              {registrationComplete.founderCount && registrationComplete.founderLimit ? (
                <span>
                  Founder spots claimed: {registrationComplete.founderCount}/
                  {registrationComplete.founderLimit}
                </span>
              ) : registrationComplete.founderNumber ? (
                <span>
                  Founder #{registrationComplete.founderNumber}
                  {registrationComplete.founderLimit
                    ? ` of ${registrationComplete.founderLimit}`
                    : ""}
                </span>
              ) : (
                <span>Your founding member access is active.</span>
              )}
            </div>
          )}

          <p>
            Check {registrationComplete.email || "your email"} for the
            verification link. You can browse while waiting, but posting,
            messaging, bidding, buying, reserving, and reviewing may require a
            verified email.
          </p>
          <p className="form-helper-text">
            While MyFarmingPal is in early access, the verification email may
            come from Firebase. Please check spam or promotions if you do not
            see it.
          </p>
          {verificationNotice && (
            <p className="auth-safe-message" role="status">
              {verificationNotice}
            </p>
          )}
          <div className="auth-check-email-actions">
            <button
              type="button"
              className="btn-primary"
              onClick={() => navigate(continueTarget)}
            >
              {continueLabel}
            </button>
            <button
              type="button"
              className="btn-soft"
              onClick={handleResendVerification}
              disabled={resendingVerification}
            >
              {resendingVerification ? "Sending..." : "Resend verification email"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const content = (
    <>
      <div className="auth-hero">
        <div className="auth-badge">Join MyFarmingPal</div>
        <h2>Create your account</h2>
        <p>
          Check local listings, messages, jobs, land access, plant listings,
          farm records, and community updates.
        </p>
      </div>

      <form onSubmit={handleRegister} className="auth-form">
        <div className="form-stack">
          <div>
            <label className="form-label" htmlFor="name">
              Name
            </label>
            <input
              id="name"
              name="name"
              type="text"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={60}
              autoComplete="name"
            />
          </div>

          <div>
            <label className="form-label" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>

          <div>
            <label className="form-label" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              placeholder="Create a password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
            />
          </div>

          <div>
            <label className="form-label" htmlFor="userType">
              Account Type
            </label>

            <select
              id="userType"
              name="userType"
              value={userType}
              onChange={(e) => setUserType(e.target.value)}
            >
              <option value="farmer">Farmer / Worker</option>
              <option value="landowner">Landowner</option>
              <option value="homesteader">Homesteader</option>
              <option value="day_worker">Day Worker</option>
              <option value="community">Community Member</option>
              <option value="event_planner">Event Planner</option>
            </select>
          </div>

          <label className="auth-checkbox">
            <input
              id="wants-notifications"
              name="wantsNotifications"
              type="checkbox"
              checked={wantsNotifications}
              onChange={(e) => setWantsNotifications(e.target.checked)}
            />
            <span>Enable notifications 🔔</span>
          </label>
        </div>

        <div className="terms-check">
          <input
            id="accepted-terms"
            name="acceptedTerms"
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => {
              setAcceptedTerms(e.target.checked);
              if (e.target.checked) setLegalError("");
            }}
            aria-describedby="legal-confirm-helper legal-confirm-error"
          />

          <label htmlFor="accepted-terms">
            I confirm that I am at least 18 years old and that I agree to the{" "}
            <Link to="/terms" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </Link>
            ,{" "}
            <Link to="/privacy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </Link>
            ,{" "}
            <Link
              to="/community-rules"
              target="_blank"
              rel="noopener noreferrer"
            >
              Community Rules
            </Link>
            , and{" "}
            <Link to="/safety" target="_blank" rel="noopener noreferrer">
              Safety Guidelines
            </Link>
            .
          </label>
        </div>
        <p id="legal-confirm-helper" className="form-helper-text">
          MyFarmingPal accounts are for adults. A parent or legal guardian may
          use their own adult account to help a teenager look for appropriate
          local work opportunities, but the adult account holder is responsible
          for the minor's use of the platform.
        </p>

        {legalError && (
          <p id="legal-confirm-error" className="auth-inline-error" role="alert">
            {legalError}
          </p>
        )}

        <button
          type="submit"
          className="btn-primary auth-submit"
          disabled={registering}
        >
          {registering ? "Creating your account..." : "Register"}
        </button>

        <p className="auth-switch">
          Already have an account?{" "}
          {embedded ? (
            <button type="button" onClick={switchToLogin}>
              Login
            </button>
          ) : (
            <Link to="/">Login</Link>
          )}
        </p>
      </form>
    </>
  );

  if (embedded) return content;

  return (
    <div className="auth-page">
      <div className="auth-card">{content}</div>
    </div>
  );
};

export default Register;
